import React from 'react';

/**
 * Unified auth chooser for LocalOps flows.
 * Defaults to account-first. LocalOps action-intent callers can opt into
 * magic-link-first with primaryAuthMethod="magic_link".
 */
export default function AuthChooser({
  headerText,
  subText,
  primaryAuthMethod = 'account',
  primaryLabel = 'Use Joe Galaxy Account',
  primaryHelper1 = 'Recommended • Earn +1000 JGK bonus',
  primaryHelper2 = 'Faster access, saved history, bonus JGK for verified actions.',
  onPrimary,
  primaryDisabled = false,
  onPrimaryStart = () => {},
  email,
  onEmailChange,
  emailError,
  onEmailErrorClear = () => {},
  onSendMagicLink,
  magicStatus = 'idle',
}) {
  const magicLinkFirst = primaryAuthMethod === 'magic_link';
  const magicButtonText =
    magicStatus === 'sending'
      ? 'Sending...'
      : magicStatus === 'sent'
      ? 'Resend magic link'
      : 'Send magic link';
  const submitMagicLink = (event) => {
    event?.preventDefault?.();
    onSendMagicLink?.();
  };

  if (!magicLinkFirst) {
    return (
      <div style={{ width: '100%' }}>
        {headerText ? (
          <div
            style={{
              marginBottom: 8,
              color: '#e8edf7',
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {headerText}
          </div>
        ) : null}
        {subText ? (
          <div
            style={{
              marginBottom: 10,
              color: '#9fb6d1',
              fontSize: 13,
            }}
          >
            {subText}
          </div>
        ) : null}

        <button
          type="button"
          className="loc-btn"
          disabled={primaryDisabled}
          onClick={() => {
            onPrimaryStart();
            if (onPrimary) onPrimary();
          }}
          style={{
            width: '100%',
            marginBottom: 10,
            padding: '12px 14px',
            borderRadius: 12,
            background:
              'linear-gradient(135deg, rgba(25,147,255,0.9), rgba(79,106,255,0.9))',
            color: '#fff',
            fontWeight: 800,
            border: 'none',
            opacity: primaryDisabled ? 0.65 : 1,
            cursor: primaryDisabled ? 'not-allowed' : 'pointer',
          }}
          data-testid="authchooser-account-primary"
        >
          {primaryLabel}
          {primaryHelper1 ? (
            <div style={{ fontSize: 12, fontWeight: 500, opacity: 0.9 }}>
              {primaryHelper1}
            </div>
          ) : null}
          {primaryHelper2 ? (
            <div style={{ fontSize: 11, fontWeight: 500, opacity: 0.75 }}>
              {primaryHelper2}
            </div>
          ) : null}
        </button>

        <form
          onSubmit={submitMagicLink}
          noValidate
          style={{
            width: '100%',
            padding: '12px 14px',
            borderRadius: 12,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            marginTop: 4,
          }}
          data-testid="authchooser-magic-secondary"
        >
          <div style={{ marginBottom: 8, color: '#c6d5ef', fontWeight: 600 }}>
            Continue with Email (Magic Link)
          </div>
          <label
            htmlFor="magic-email"
            style={{
              display: 'block',
              fontSize: 12,
              color: '#8fa3c4',
              marginBottom: 6,
            }}
          >
            Email address (no password required)
          </label>
          <input
            id="magic-email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => {
              onEmailChange?.(e.target.value);
              if (emailError) onEmailErrorClear();
            }}
            placeholder="you@example.com"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
            style={{
              width: '100%',
              borderRadius: 10,
              border: `1px solid ${
                emailError ? 'rgba(255,99,99,0.7)' : 'rgba(255,255,255,0.18)'
              }`,
              background: 'rgba(255,255,255,0.06)',
              color: '#e8edf7',
              padding: '12px 12px',
              outline: 'none',
              fontSize: 14,
              marginBottom: 8,
            }}
          />
          {emailError ? (
            <div style={{ color: '#ff9b9b', fontSize: 12, marginBottom: 8 }}>
              {emailError}
            </div>
          ) : null}
          <button
            type="submit"
            className="loc-btn secondary localops-btn"
            disabled={magicStatus === 'sending'}
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: 12,
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.14))',
              color: '#e8edf7',
              border: '1px solid rgba(255,255,255,0.5)',
              fontWeight: 700,
            }}
          >
            {magicButtonText}
            <div style={{ fontSize: 12, fontWeight: 500, opacity: 0.9 }}>
              We’ll email a link that brings you back here.
            </div>
          </button>
        </form>
      </div>
    );
  }

  const accountButtonStyle = (isPrimary) =>
    isPrimary
      ? {
          width: '100%',
          marginBottom: 10,
          padding: '12px 14px',
          borderRadius: 12,
          background:
            'linear-gradient(135deg, rgba(25,147,255,0.9), rgba(79,106,255,0.9))',
          color: '#fff',
          fontWeight: 800,
          border: 'none',
          opacity: primaryDisabled ? 0.65 : 1,
          cursor: primaryDisabled ? 'not-allowed' : 'pointer',
        }
      : {
          width: '100%',
          padding: '12px 14px',
          borderRadius: 12,
          background: 'rgba(255,255,255,0.04)',
          color: '#e8edf7',
          fontWeight: 700,
          border: '1px solid rgba(255,255,255,0.22)',
          opacity: primaryDisabled ? 0.65 : 1,
          cursor: primaryDisabled ? 'not-allowed' : 'pointer',
        };

  const magicButtonStyle = (isPrimary) =>
    isPrimary
      ? {
          width: '100%',
          padding: '12px 14px',
          borderRadius: 12,
          background:
            'linear-gradient(135deg, rgba(25,147,255,0.9), rgba(79,106,255,0.9))',
          color: '#fff',
          border: 'none',
          fontWeight: 800,
        }
      : {
          width: '100%',
          padding: '12px 14px',
          borderRadius: 12,
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.14))',
          color: '#e8edf7',
          border: '1px solid rgba(255,255,255,0.5)',
          fontWeight: 700,
        };

  const renderAccountOption = (isPrimary) => {
    const button = (
      <button
        type="button"
        className={isPrimary ? 'loc-btn' : 'loc-btn secondary localops-btn'}
        disabled={primaryDisabled}
        onClick={() => {
          onPrimaryStart();
          if (onPrimary) onPrimary();
        }}
        style={accountButtonStyle(isPrimary)}
        data-testid={
          isPrimary ? 'authchooser-account-primary' : 'authchooser-account-secondary'
        }
      >
        {primaryLabel}
        {isPrimary && primaryHelper1 ? (
          <div style={{ fontSize: 12, fontWeight: 500, opacity: 0.9 }}>
            {primaryHelper1}
          </div>
        ) : null}
        {primaryHelper2 ? (
          <div style={{ fontSize: 11, fontWeight: 500, opacity: 0.75 }}>
            {primaryHelper2}
          </div>
        ) : null}
      </button>
    );

    if (isPrimary) return button;

    return (
      <div
        style={{
          width: '100%',
          padding: '12px 14px',
          borderRadius: 12,
          background: 'rgba(255,255,255,0.035)',
          border: '1px solid rgba(255,255,255,0.08)',
          marginTop: 10,
        }}
      >
        <div style={{ marginBottom: 8, color: '#c6d5ef', fontWeight: 600 }}>
          Already use Joe Galaxy?
        </div>
        {button}
      </div>
    );
  };

  const renderMagicLinkOption = (isPrimary) => (
    <form
      onSubmit={submitMagicLink}
      noValidate
      style={{
        width: '100%',
        padding: '12px 14px',
        borderRadius: 12,
        background: isPrimary
          ? 'rgba(25,147,255,0.08)'
          : 'rgba(255,255,255,0.04)',
        border: isPrimary
          ? '1px solid rgba(116,185,255,0.32)'
          : '1px solid rgba(255,255,255,0.08)',
        marginTop: isPrimary ? 0 : 4,
        marginBottom: isPrimary ? 10 : 0,
      }}
      data-testid={
        isPrimary ? 'authchooser-magic-primary' : 'authchooser-magic-secondary'
      }
    >
      <div style={{ marginBottom: 8, color: '#c6d5ef', fontWeight: 600 }}>
        {isPrimary ? 'Continue with Email' : 'Continue with Email (Magic Link)'}
      </div>
      <div
        style={{
          color: '#9fb6d1',
          fontSize: 12,
          marginBottom: 8,
          lineHeight: 1.4,
        }}
      >
        {isPrimary
          ? "We'll email a secure link that brings you back to this LocalOps action."
          : "We'll email a link that brings you back here."}
      </div>
      <label
        htmlFor="magic-email"
        style={{
          display: 'block',
          fontSize: 12,
          color: '#8fa3c4',
          marginBottom: 6,
        }}
      >
        Email address (no password required)
      </label>
      <input
        id="magic-email"
        name="email"
        type="email"
        value={email}
        onChange={(e) => {
          onEmailChange?.(e.target.value);
          if (emailError) onEmailErrorClear();
        }}
        placeholder="you@example.com"
        autoComplete="email"
        inputMode="email"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck="false"
        style={{
          width: '100%',
          borderRadius: 10,
          border: `1px solid ${
            emailError ? 'rgba(255,99,99,0.7)' : 'rgba(255,255,255,0.18)'
          }`,
          background: 'rgba(255,255,255,0.06)',
          color: '#e8edf7',
          padding: '12px 12px',
          outline: 'none',
          fontSize: 14,
          marginBottom: 8,
        }}
      />
      {emailError ? (
        <div style={{ color: '#ff9b9b', fontSize: 12, marginBottom: 8 }}>
          {emailError}
        </div>
      ) : null}
      <button
        type="submit"
        className={isPrimary ? 'loc-btn' : 'loc-btn secondary localops-btn'}
        disabled={magicStatus === 'sending'}
        style={magicButtonStyle(isPrimary)}
      >
        {magicButtonText}
      </button>
    </form>
  );

  return (
    <div style={{ width: '100%' }}>
      {headerText ? (
        <div
          style={{
            marginBottom: 8,
            color: '#e8edf7',
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          {headerText}
        </div>
      ) : null}
      {subText ? (
        <div
          style={{
            marginBottom: 10,
            color: '#9fb6d1',
            fontSize: 13,
          }}
        >
          {subText}
        </div>
      ) : null}

      {magicLinkFirst ? (
        <>
          {renderMagicLinkOption(true)}
          {renderAccountOption(false)}
        </>
      ) : (
        <>
          {renderAccountOption(true)}
          {renderMagicLinkOption(false)}
        </>
      )}
    </div>
  );
}
